import { isIE } from "./common";

type DatadogRum = typeof import("@datadog/browser-rum")["datadogRum"];
type DatadogLogs = typeof import("@datadog/browser-logs")["datadogLogs"];

let initializedPromise: Promise<void> | null = null;
let rum: DatadogRum | null = null;
let logs: DatadogLogs | null = null;

// main バンドルがすでに 3MB を超えているため、静的 import せず別チャンクに
// 切り出す。SDK 7 は IE 非対応なので isIE() では読み込まない
const loadSdk = () =>
  Promise.all([
    import("@datadog/browser-rum"),
    import("@datadog/browser-logs")
  ]);

export const initDatadog = (): Promise<void> => {
  if (typeof window === "undefined" || isIE()) {
    return Promise.resolve();
  }

  if (!DATADOG_APPLICATION_ID || !DATADOG_CLIENT_TOKEN) {
    return Promise.resolve();
  }

  if (!initializedPromise) {
    initializedPromise = loadSdk()
      .then(([{ datadogRum }, { datadogLogs }]) => {
        rum = datadogRum;
        logs = datadogLogs;

        const common = {
          clientToken: DATADOG_CLIENT_TOKEN,
          site: DATADOG_SITE,
          service: DATADOG_SERVICE,
          env: DATADOG_ENV,
          version: VERSION_HASH
        };

        datadogRum.init({
          ...common,
          applicationId: DATADOG_APPLICATION_ID,
          sessionSampleRate: 100,
          sessionReplaySampleRate: 20,
          trackUserInteractions: true,
          trackResources: true,
          trackLongTasks: true,
          defaultPrivacyLevel: "mask-user-input"
        });

        datadogLogs.init({
          ...common,
          forwardErrorsToLogs: true,
          sessionSampleRate: 100
        });
      })
      .catch(error => {
        initializedPromise = null;
        rum = null;
        logs = null;

        if (IS_DEBUG) {
          console.error(error);
        }
      });
  }

  return initializedPromise;
};

// userId が空のときはログアウトなので紐づけを解除する
export const setDatadogUser = (userId: string): Promise<void> =>
  initializedPromise
    ? initializedPromise.then(() => {
        if (!rum || !logs) {
          return;
        }

        if (userId) {
          rum.setUser({ id: userId });
          logs.setUser({ id: userId });
        } else {
          rum.clearUser();
          logs.clearUser();
        }
      })
    : Promise.resolve();
